export type ConnectorPoint = {
  x: number;
  y: number;
};

export type ConnectorTargetBounds = {
  left: number;
  right: number;
  top: number;
};

type StatusConnectorLayout = {
  statusAnchor: ConnectorPoint;
  statBounds: ConnectorTargetBounds[];
  statusLaneY: number;
  dividerX: number;
  statLaneY: number;
};

export type StatusConnectorRoute = {
  path: string;
  start: ConnectorPoint;
  ends: ConnectorPoint[];
};

const pixel = (value: number): number => Math.round(value);

/**
 * Builds one non-overlapping orthogonal route through the whitespace reserved
 * above the status buttons, along the panel divider, and above the stat grid.
 */
export function routeStatusConnector({
  statusAnchor,
  statBounds,
  statusLaneY,
  dividerX,
  statLaneY,
}: StatusConnectorLayout): StatusConnectorRoute {
  const start = {
    x: pixel(statusAnchor.x),
    y: pixel(statusAnchor.y),
  };
  const ends = statBounds.map((bounds) => ({
    x: pixel((bounds.left + bounds.right) / 2),
    y: pixel(bounds.top),
  }));

  if (ends.length === 0) {
    return { path: '', start, ends };
  }

  const roundedStatusLaneY = pixel(statusLaneY);
  const roundedDividerX = pixel(dividerX);
  const roundedStatLaneY = pixel(statLaneY);
  const furthestTargetX = Math.min(...ends.map(({ x }) => x));
  const trunk = [
    `M ${start.x} ${start.y}`,
    `V ${roundedStatusLaneY}`,
    `H ${roundedDividerX}`,
    `V ${roundedStatLaneY}`,
    `H ${furthestTargetX}`,
  ];
  const branches = ends.map(
    ({ x, y }) => `M ${x} ${roundedStatLaneY} V ${y}`,
  );

  return {
    path: [...trunk, ...branches].join(' '),
    start,
    ends,
  };
}
